import React, { useState } from 'react';
import { Wrench, ChevronRight, ChevronDown, Loader2, Sparkles, X } from 'lucide-react';
import type { ToolCallInfo } from '../../api';

const toggleToolExpand = (id: string, prev: Set<string>): Set<string> => {
  const next = new Set(prev);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
};

const formatDuration = (start: number, end?: number) => {
  const duration = (end || Date.now()) - start;
  return duration < 1000 ? `${duration}ms` : `${(duration / 1000).toFixed(1)}s`;
};

const formatJson = (obj: unknown) => {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
};

/** Visual styling for each 3-phase tool call state. */
const STATE_STYLES: Record<ToolCallInfo['status'], { border: string; bg: string; icon: string }> = {
  generating: { border: 'border-amber-300', bg: 'bg-amber-50', icon: 'text-amber-600' },
  calling: { border: 'border-blue-300', bg: 'bg-blue-50', icon: 'text-blue-600' },
  completed: { border: 'border-slate-200', bg: 'bg-white', icon: 'text-green-600' },
};

const STATE_LABEL: Record<ToolCallInfo['status'], string> = {
  generating: '생성중',
  calling: '실행중',
  completed: '', // duration rendered instead
};

/**
 * ToolCallPanel — renders every tool call from the SSE stream, with 3 states:
 *
 *   generating → calling → completed
 *
 * Filters by `currentMessageId` so only the active assistant message's tools
 * are shown (multi-turn chats don't collapse earlier tool results into the
 * current panel). Each tool card carries `data-testid` + `data-state` for
 * Playwright assertions (see spec §6 dashboard E2E).
 */
const ToolCallPanel: React.FC<{
  toolCalls: ToolCallInfo[];
  isExpanded: boolean;
  onToggle: () => void;
  /** Message ID whose tools should be shown. When undefined, all tools render. */
  currentMessageId?: string;
  /** Reset the per-message filter so every turn's tools are visible. */
  onClearFilter?: () => void;
}> = ({ toolCalls, isExpanded, onToggle, currentMessageId, onClearFilter }) => {
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());

  const visibleTools = currentMessageId
    ? toolCalls.filter((tc) => tc.messageId === currentMessageId)
    : toolCalls;

  if (visibleTools.length === 0) {
    return null;
  }

  const hasActive = visibleTools.some((t) => t.status === 'generating' || t.status === 'calling');

  return (
    <div
      className="border-l border-slate-200 bg-slate-50 flex flex-col relative"
      style={{ width: isExpanded ? '360px' : '48px' }}
    >
      <button
        onClick={onToggle}
        className="p-3 border-b border-slate-200 hover:bg-slate-100 transition-colors flex items-center gap-2"
        title={isExpanded ? '패널 접기' : '도구 호출 보기'}
      >
        <Wrench size={18} className="text-slate-600" />
        {isExpanded && <span className="text-sm font-medium text-slate-700">도구 호출</span>}
        {!isExpanded && hasActive && (
          <span className="absolute top-2 right-2 w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
        )}
      </button>

      {isExpanded && currentMessageId && onClearFilter && (
        <div className="px-3 py-1 border-b border-slate-200 flex items-center gap-2 text-xs bg-amber-50">
          <span className="text-amber-700">특정 메시지 도구만 표시 중</span>
          <button
            onClick={onClearFilter}
            data-testid="tool-panel-clear-filter"
            className="ml-auto flex items-center gap-1 text-slate-600 hover:text-slate-900"
            title="모든 도구 보기"
          >
            <X size={12} />
            전체
          </button>
        </div>
      )}

      {isExpanded && (
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {visibleTools.map((tool) => {
            const style = STATE_STYLES[tool.status];
            return (
              <div
                key={tool.id}
                data-testid={`tool-status-${tool.name}`}
                data-state={tool.status}
                className={`rounded-lg border ${style.border} ${style.bg}`}
              >
                <button
                  onClick={() => setExpandedTools((prev) => toggleToolExpand(tool.id, prev))}
                  className="w-full px-3 py-2 flex items-center gap-2 text-left"
                >
                  {expandedTools.has(tool.id) ? (
                    <ChevronDown size={14} className="text-slate-400" />
                  ) : (
                    <ChevronRight size={14} className="text-slate-400" />
                  )}
                  {tool.status === 'generating' ? (
                    <Sparkles size={14} className={style.icon} />
                  ) : (
                    <Wrench size={14} className={style.icon} />
                  )}
                  <span className="font-mono text-xs font-medium text-slate-700 flex-1 truncate">
                    {tool.name}
                  </span>
                  <span
                    className={`text-xs ${
                      tool.status === 'generating'
                        ? 'text-amber-700'
                        : tool.status === 'calling'
                        ? 'text-blue-600'
                        : 'text-slate-400'
                    }`}
                  >
                    {tool.status === 'generating' || tool.status === 'calling' ? (
                      <span className="flex items-center gap-1">
                        <Loader2 size={10} className="animate-spin" />
                        {STATE_LABEL[tool.status]}
                      </span>
                    ) : (
                      formatDuration(tool.startTime, tool.endTime)
                    )}
                  </span>
                </button>

                {expandedTools.has(tool.id) && (
                  <div className="px-3 pb-3 space-y-2">
                    {/* Generating phase has no input yet — make that explicit so
                        empty Input block isn't confusing. */}
                    {tool.status === 'generating' ? (
                      <div className="text-xs text-amber-700 italic">
                        LLM이 파라미터를 생성하고 있습니다...
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="text-[10px] uppercase text-slate-400 font-medium mb-1">Input</div>
                          <pre className="text-xs bg-slate-800 text-green-400 p-2 rounded overflow-x-auto max-h-32">
                            {formatJson(tool.arguments)}
                          </pre>
                        </div>
                        {tool.result && (
                          <div>
                            <div className="text-[10px] uppercase text-slate-400 font-medium mb-1">Output</div>
                            <pre className="text-xs bg-slate-800 text-amber-400 p-2 rounded overflow-x-auto max-h-48">
                              {tool.result.length > 500 ? tool.result.slice(0, 500) + '...' : tool.result}
                            </pre>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isExpanded && visibleTools.length > 0 && (
        <div className="p-2 text-center">
          <span className="text-xs text-slate-500 font-medium">{visibleTools.length}</span>
        </div>
      )}
    </div>
  );
};

export default ToolCallPanel;
